/**
 * The editor command registry (ADR-009 G15, ADR-008 phase 5): menu, palette
 * and dialog operations as data with typed parameters.
 *
 * A command is reached through one intent, `command.run { command, params }`,
 * so it inherits everything the semantic layer gives intents: validation with
 * the actor's policy, one `TransactionCommand` per run (one history entry, one
 * revision), the revision ledger, the field journal and revert. The UI runs a
 * command as the user (`runCommand`, synchronous) and an agent runs the same
 * entry through `maipo_apply`; parameter schemas live in
 * `@maipo/agent-protocol` (`maipo-commands-0.1-draft.schema.json`) and the
 * registry test fails if a command and its schema drift apart.
 *
 * Commands must keep the native contract of `CompiledStep`: native commands
 * validate fully before mutating, so a rejected run leaves nothing behind.
 */
import type { FieldChange, LayerId } from '@maipo/agent-protocol';
import type { EditorDocument } from '../../core/model';
import type { DocumentSession } from '../../core/session';
import type { CompiledStep, Diagnostic, Footprint, PrepareContext, ValidateContext } from '../registry';

/** Footprint keys of document resources that are not layers. Disjoint keys never conflict. */
export const DOCUMENT_RESOURCES = Object.freeze({
  guides: '#guides',
  notes: '#notes',
  selection: '#selection',
  channels: '#channels',
  artboards: '#artboards',
  slices: '#slices',
  exportAssets: '#export-assets',
  perspective: '#perspective',
  actions: '#actions',
  presets: '#presets',
  swatches: '#swatches',
  comps: '#layer-comps',
  styles: '#graphic-styles',
  publicResources: '#public-resources',
} as const);

export type CommandGroup = 'document' | 'guides' | 'selection' | 'pixels' | 'filters' | 'layers' | 'vector' | 'text' | 'resources' | 'history';

export interface EditorCommand<P = Record<string, unknown>> {
  /** Stable id, e.g. "document.rotate". Must have a params schema of the same id. */
  id: string;
  group: CommandGroup;
  /**
   * Inventory ids (docs/editor/agent-parity-inventory.json) this command
   * reproduces, for example "action.rotate-cw" or "dialog.geometry.canvas".
   */
  backs: readonly string[];
  /** Runs a worker or decoder before commit (`prepare`); the UI must use the asynchronous path. */
  asynchronous?: boolean;
  /**
   * Adds layers: counts toward a transaction's layer-creation limits, like
   * layer.create. `true` is one layer; a function decides from the params
   * and the document (an optional output layer, the steps of a recorded
   * action) and may return a count.
   */
  createsLayer?: boolean | ((params: P, document: EditorDocument) => boolean | number);
  /**
   * Estimated work in pixel operations (pixels touched, times passes), for
   * the per-transaction compute budget of agents and add-ons
   * (`AGENT_BUDGETS.pixelOpsPerTransaction`). Commands whose cost scales
   * with their parameters or the layer size declare it; others count as
   * cheap.
   */
  cost?(params: P, document: EditorDocument): number;
  /**
   * History label, before the actor prefix. It must equal the label of the
   * native command the UI executed before, so undo labels do not change.
   */
  label(params: P, document: EditorDocument): string;
  footprint(document: EditorDocument, params: P, aliases: ReadonlyMap<string, LayerId>): Footprint;
  /** Pure checks: existence, kinds, locks (with `context.policy`), limits. */
  validate(document: EditorDocument, params: P, context: ValidateContext): Diagnostic[];
  /** `context.assets` resolves AssetInputs in `params` (for example the bridge's staged images for this call). */
  prepare?(session: DocumentSession, params: P, signal: AbortSignal, context?: PrepareContext): Promise<unknown>;
  /** Whether these params need `prepare` against `document` (default: whenever `prepare` exists). */
  needsPrepare?(params: P, document: EditorDocument): boolean;
  /** Releases what `prepare` returned after the dispatch, committed or not (see CommandDefinition.releasePrepared). */
  releasePrepared?(params: P, prepared: unknown): void;
  compile(document: EditorDocument, params: P, prepared: unknown, aliases: Map<string, LayerId>): CompiledStep[];
  /**
   * Param defaults for actors other than the user and replays (agents,
   * add-ons). Intents never depend on the user's implicit UI state
   * (ADR-009 §3.1), so a param whose UI default reads it (for example the
   * current pixel selection) defaults to an explicit value for agents.
   */
  agentDefaults?: Partial<P>;
  /** Predicted changes for dry runs of documents too large to clone. Default: none. */
  describe?(document: EditorDocument, params: P, aliases: ReadonlyMap<string, LayerId>): FieldChange[];
}

export function defineCommand<P>(command: EditorCommand<P>): EditorCommand {
  return command as unknown as EditorCommand;
}

export type CommandRegistry = ReadonlyMap<string, EditorCommand>;

export function createCommandRegistry(commands: readonly EditorCommand[]): CommandRegistry {
  const registry = new Map<string, EditorCommand>();
  for (const command of commands) {
    if (registry.has(command.id)) throw new Error(`Duplicate editor command: ${command.id}`);
    registry.set(command.id, command);
  }
  return registry;
}

export function documentFootprint(): Footprint {
  return { reads: [], writes: [], structural: true, document: true };
}

export function resourceFootprint(...keys: string[]): Footprint {
  return { reads: [...keys], writes: [...keys], structural: false, document: false };
}
